// DashboardThreatMap.js - Dashboard Variation C: "Threat Map"
// Visual/graphical centerpiece with interactive elements

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    getCommandCenterSummary,
    getRiskLevelColor,
    getRiskLevelLabel,
    formatRelativeTime,
    getSeverityColor
} from '../../services/commandCenterService';
import { SkeletonDashboard } from '../../components/common';
import './DashboardThreatMap.css';

// Asset Node component - represents an asset on the map
function AssetNode({ asset, isSelected, onClick, position }) {
    const getRiskClass = (level) => {
        if (level === 'critical') return 'node-critical';
        if (level === 'atRisk') return 'node-atrisk';
        if (level === 'caution') return 'node-caution';
        return 'node-healthy';
    };

    const getNodeSize = () => {
        const totalVulns = (asset.vulnCounts?.critical || 0) +
                          (asset.vulnCounts?.high || 0) +
                          (asset.vulnCounts?.medium || 0);
        if (totalVulns > 10) return 'node-large';
        if (totalVulns > 5) return 'node-medium';
        return 'node-small';
    };

    return (
        <div
            className={`tm-node ${getRiskClass(asset.riskLevel)} ${getNodeSize()} ${isSelected ? 'selected' : ''}`}
            style={{ left: position.x, top: position.y }}
            onClick={() => onClick(asset)}
        >
            <div className="node-icon">
                {asset.assetType === 'domain' ? '🌐' : '🖥️'}
            </div>
            <div className="node-label">{asset.value}</div>
            {asset.riskLevel === 'critical' && (
                <div className="node-alert-pulse" />
            )}
        </div>
    );
}

// Metric panel component
function MetricPanel({ title, value, icon, color, onClick, position }) {
    return (
        <div
            className={`tm-metric-panel ${position}`}
            onClick={onClick}
            style={{ '--panel-color': color }}
        >
            <div className="panel-icon">{icon}</div>
            <div className="panel-value" style={{ color }}>{value}</div>
            <div className="panel-title">{title}</div>
        </div>
    );
}

// Selected asset detail panel
function AssetDetailPanel({ asset, onClose, onNavigate }) {
    if (!asset) return null;

    return (
        <div className="tm-detail-panel">
            <button className="detail-close" onClick={onClose}>×</button>

            <div className="detail-header">
                <span className="detail-icon">
                    {asset.assetType === 'domain' ? '🌐' : '🖥️'}
                </span>
                <div className="detail-info">
                    <h3>{asset.value}</h3>
                    <span className="detail-type">{asset.assetType?.toUpperCase()}</span>
                </div>
            </div>

            <div className="detail-status" style={{ borderColor: getRiskLevelColor(asset.riskLevel) }}>
                <span className="status-label">Status:</span>
                <span className="status-value" style={{ color: getRiskLevelColor(asset.riskLevel) }}>
                    {getRiskLevelLabel(asset.riskLevel)}
                </span>
                <span className="status-score">Score: {asset.riskScore}/100</span>
            </div>

            <div className="detail-findings">
                <h4>Findings</h4>
                <div className="findings-grid">
                    <div className="finding-item critical">
                        <span className="finding-count">{asset.vulnCounts?.critical || 0}</span>
                        <span className="finding-label">Critical</span>
                    </div>
                    <div className="finding-item high">
                        <span className="finding-count">{asset.vulnCounts?.high || 0}</span>
                        <span className="finding-label">High</span>
                    </div>
                    <div className="finding-item medium">
                        <span className="finding-count">{asset.vulnCounts?.medium || 0}</span>
                        <span className="finding-label">Medium</span>
                    </div>
                    <div className="finding-item low">
                        <span className="finding-count">{asset.vulnCounts?.low || 0}</span>
                        <span className="finding-label">Low</span>
                    </div>
                </div>
            </div>

            <div className="detail-meta">
                <div className="meta-row">
                    <span>Last Scan:</span>
                    <span>{formatRelativeTime(asset.lastScanAt)}</span>
                </div>
                <div className="meta-row">
                    <span>Trend:</span>
                    <span className={`trend-${asset.trend}`}>
                        {asset.trend === 'improving' ? '↑ Improving' :
                         asset.trend === 'worsening' ? '↓ Worsening' : '→ Stable'}
                    </span>
                </div>
            </div>

            <div className="detail-actions">
                <button
                    className="action-btn primary"
                    onClick={() => onNavigate(`/assets/${asset.assetId}`)}
                >
                    View Full Profile
                </button>
                <button
                    className="action-btn"
                    onClick={() => onNavigate(`/assets/${asset.assetId}#vulnerabilities`)}
                >
                    See Findings
                </button>
            </div>
        </div>
    );
}

// Connection lines between nodes (SVG)
function ConnectionLines({ assets, centerX, centerY }) {
    return (
        <svg className="tm-connections" width="100%" height="100%">
            <defs>
                <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="var(--border-medium)" stopOpacity="0.8" />
                    <stop offset="100%" stopColor="var(--border-dark)" stopOpacity="0.3" />
                </linearGradient>
            </defs>
            {assets.map((asset, i) => {
                const angle = (i / assets.length) * 2 * Math.PI - Math.PI / 2;
                const radius = 180;
                const x = centerX + Math.cos(angle) * radius;
                const y = centerY + Math.sin(angle) * radius;

                return (
                    <line
                        key={asset.assetId}
                        x1={centerX}
                        y1={centerY}
                        x2={x}
                        y2={y}
                        stroke="url(#lineGradient)"
                        strokeWidth="1"
                        className={`connection-line ${asset.riskLevel === 'critical' ? 'critical' : ''}`}
                    />
                );
            })}
        </svg>
    );
}

function DashboardThreatMap() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState({
        summary: {},
        assets: [],
        topRisks: [],
        trends: {}
    });
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [mapDimensions] = useState({ width: 600, height: 500 });

    // Fetch data
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const result = await getCommandCenterSummary();
                if (result.success) {
                    setData({
                        summary: result.summary || {},
                        assets: result.assets || [],
                        topRisks: result.topRisks || [],
                        trends: result.trends || {}
                    });
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const { summary, assets, topRisks } = data;

    // Calculate node positions in a circular layout
    const nodePositions = useMemo(() => {
        const centerX = mapDimensions.width / 2;
        const centerY = mapDimensions.height / 2;
        const radius = Math.min(centerX, centerY) - 80;

        return assets.slice(0, 12).map((asset, i, arr) => {
            const angle = (i / arr.length) * 2 * Math.PI - Math.PI / 2;
            return {
                asset,
                position: {
                    x: centerX + Math.cos(angle) * radius - 40,
                    y: centerY + Math.sin(angle) * radius - 40
                }
            };
        });
    }, [assets, mapDimensions]);

    // Handle asset click
    const handleAssetClick = useCallback((asset) => {
        setSelectedAsset(asset === selectedAsset ? null : asset);
    }, [selectedAsset]);

    // Calculate overall risk level
    const riskLevel = useMemo(() => {
        const score = summary.overallRiskScore || 0;
        if (score >= 80) return 'healthy';
        if (score >= 60) return 'caution';
        if (score >= 40) return 'atRisk';
        return 'critical';
    }, [summary.overallRiskScore]);

    if (loading) {
        return (
            <div className="tm-page">
                <header className="tm-header">
                    <Link to="/dashboard-prototypes" className="back-link">← Prototypes</Link>
                    <h1>Threat Map</h1>
                </header>
                <SkeletonDashboard />
            </div>
        );
    }

    if (error) {
        return (
            <div className="tm-page">
                <header className="tm-header">
                    <Link to="/dashboard-prototypes" className="back-link">← Prototypes</Link>
                    <h1>Threat Map</h1>
                </header>
                <div className="tm-error">
                    <span className="error-icon">⚠️</span>
                    <p>{error}</p>
                    <button onClick={() => window.location.reload()}>Retry</button>
                </div>
            </div>
        );
    }

    const centerX = mapDimensions.width / 2;
    const centerY = mapDimensions.height / 2;

    return (
        <div className="tm-page">
            {/* Header */}
            <header className="tm-header">
                <Link to="/dashboard-prototypes" className="back-link">← Prototypes</Link>
                <h1>
                    <span className="header-icon">🗺️</span>
                    Threat Map
                </h1>
                <div className="header-meta">
                    <span className="last-update">
                        Updated: {formatRelativeTime(summary.lastScanAt)}
                    </span>
                </div>
            </header>

            {/* Main Layout */}
            <div className="tm-layout">
                {/* Left Metrics */}
                <div className="tm-side-metrics left">
                    <MetricPanel
                        title="Critical"
                        value={summary.totalVulnerabilities?.critical || 0}
                        icon="🔴"
                        color="var(--severity-critical)"
                        position="left"
                        onClick={() => navigate('/inventory?severity=critical')}
                    />
                    <MetricPanel
                        title="High"
                        value={summary.totalVulnerabilities?.high || 0}
                        icon="🟠"
                        color="var(--severity-high)"
                        position="left"
                        onClick={() => navigate('/inventory?severity=high')}
                    />
                    <MetricPanel
                        title="Medium"
                        value={summary.totalVulnerabilities?.medium || 0}
                        icon="🟡"
                        color="var(--severity-medium)"
                        position="left"
                        onClick={() => navigate('/inventory?severity=medium')}
                    />
                </div>

                {/* Central Map Area */}
                <div className="tm-map-container">
                    <div className="tm-map" style={{ width: mapDimensions.width, height: mapDimensions.height }}>
                        {/* Connection lines */}
                        <ConnectionLines
                            assets={assets.slice(0, 12)}
                            centerX={centerX}
                            centerY={centerY}
                        />

                        {/* Central hub */}
                        <div
                            className={`tm-central-hub ${riskLevel}`}
                            style={{
                                left: centerX - 60,
                                top: centerY - 60
                            }}
                        >
                            <div className="hub-score">{summary.overallRiskScore || 0}</div>
                            <div className="hub-label">SECURITY</div>
                            <div className="hub-sublabel">SCORE</div>
                        </div>

                        {/* Asset nodes */}
                        {nodePositions.map(({ asset, position }) => (
                            <AssetNode
                                key={asset.assetId}
                                asset={asset}
                                position={position}
                                isSelected={selectedAsset?.assetId === asset.assetId}
                                onClick={handleAssetClick}
                            />
                        ))}
                    </div>

                    {/* Legend */}
                    <div className="tm-legend">
                        <div className="legend-item">
                            <span className="legend-dot critical"></span>
                            <span>Critical</span>
                        </div>
                        <div className="legend-item">
                            <span className="legend-dot atrisk"></span>
                            <span>At Risk</span>
                        </div>
                        <div className="legend-item">
                            <span className="legend-dot caution"></span>
                            <span>Caution</span>
                        </div>
                        <div className="legend-item">
                            <span className="legend-dot healthy"></span>
                            <span>Healthy</span>
                        </div>
                    </div>
                </div>

                {/* Right Metrics */}
                <div className="tm-side-metrics right">
                    <MetricPanel
                        title="Total Assets"
                        value={summary.totalAssets || 0}
                        icon="🌐"
                        color="var(--neon-blue)"
                        position="right"
                        onClick={() => navigate('/assets')}
                    />
                    <MetricPanel
                        title="At Risk"
                        value={(summary.riskDistribution?.critical || 0) + (summary.riskDistribution?.atRisk || 0)}
                        icon="⚠️"
                        color="var(--severity-high)"
                        position="right"
                    />
                    <MetricPanel
                        title="Healthy"
                        value={summary.riskDistribution?.healthy || 0}
                        icon="✓"
                        color="var(--severity-low)"
                        position="right"
                    />
                </div>

                {/* Detail Panel (slides in when asset selected) */}
                {selectedAsset && (
                    <AssetDetailPanel
                        asset={selectedAsset}
                        onClose={() => setSelectedAsset(null)}
                        onNavigate={navigate}
                    />
                )}
            </div>

            {/* Bottom: Top Risks Quick View */}
            <div className="tm-bottom-panel">
                <h3>
                    <span className="panel-icon">🎯</span>
                    Priority Targets
                </h3>
                <div className="priority-targets">
                    {topRisks.slice(0, 5).map((risk, i) => (
                        <div
                            key={i}
                            className="priority-target"
                            onClick={() => navigate(`/assets/${risk.assetId}#vulnerabilities`)}
                        >
                            <span className="target-rank">{i + 1}</span>
                            <div className="target-info">
                                <span className="target-title">{risk.title || risk.name}</span>
                                <span className="target-asset">{risk.assetValue || risk.asset}</span>
                            </div>
                            <span
                                className="target-severity"
                                style={{ color: getSeverityColor(risk.severity) }}
                            >
                                {risk.severity?.toUpperCase()}
                            </span>
                        </div>
                    ))}
                    {topRisks.length === 0 && (
                        <div className="empty-targets">
                            No priority targets identified
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default DashboardThreatMap;
